import {
  GetGiftCardReq,
  GetGiftCardRes,
  GetGiftCardByNumberReq,
  GetGiftCardByNumberRes,
  ListGiftCardHistoryRes,
  ListGiftCardHistoryReq,
  BulkGenerateGiftCardNumbersReq,
  BulkGenerateGiftCardNumbersRes,
  BulkImportGiftCardNumbersReq,
  BulkImportGiftCardNumbersRes,
  GetGiftCardHistoryPDFReq,
  GetGiftCardHistoryPDFRes,
  DeactivateGiftCardReq,
  DeactivateGiftCardRes,
  EnableOrDisableGiftCardsReq,
  EnableOrDisableGiftCardsRes,
} from "gen/api/giftcard/service_pb"
import { GiftCardServiceClient } from "gen/api/giftcard/service_pb.client"
import { UUID } from "shared-ts/classes/uuid"

import { apiSlice, transport } from "./apiSlice"
import { companyApiSlice } from "./company"

const client = new GiftCardServiceClient(transport)

export const giftcardsApiSlice = apiSlice
  .enhanceEndpoints({
    addTagTypes: [
      "GiftCard",
      "GiftCardHistory",
      "GetGiftCardHistoryPDF",
      "Company",
    ],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getGiftCard: builder.query<GetGiftCardRes, GetGiftCardReq>({
        query: (r) => client.getGiftCard(r),
        providesTags: (_response, _error, request) => [
          {
            type: "GiftCard",
            id: UUID.idString(request.id),
          },
        ],
      }),
      getGiftCardByNumber: builder.query<
        GetGiftCardByNumberRes,
        GetGiftCardByNumberReq
      >({
        query: (r) => client.getGiftCardByNumber(r),
      }),
      listGiftCardHistory: builder.query<
        ListGiftCardHistoryRes,
        ListGiftCardHistoryReq
      >({
        query: (r) => client.listGiftCardHistory(r),
        providesTags: (_response, _error, request) => [
          { type: "GiftCardHistory", id: UUID.idString(request.id) },
        ],
      }),
      bulkGenerateGiftCardNumbers: builder.mutation<
        BulkGenerateGiftCardNumbersRes,
        BulkGenerateGiftCardNumbersReq
      >({
        query: (r) => client.bulkGenerateGiftCardNumbers(r),
      }),
      bulkImportGiftCardNumbers: builder.mutation<
        BulkImportGiftCardNumbersRes,
        BulkImportGiftCardNumbersReq
      >({
        query: (r) => client.bulkImportGiftCardNumbers(r),
      }),
      getGiftCardHistoryPDF: builder.query<
        GetGiftCardHistoryPDFRes,
        GetGiftCardHistoryPDFReq
      >({
        query: (r) => client.getGiftCardHistoryPDF(r),
        providesTags: (_response, _error, request) => [
          { type: "GetGiftCardHistoryPDF", id: UUID.idString(request.id) },
        ],
      }),
      deactivateGiftCard: builder.mutation<
        DeactivateGiftCardRes,
        DeactivateGiftCardReq
      >({
        query: (r) => client.deactivateGiftCard(r),
        invalidatesTags: (_response, _error, request) => [
          { type: "GiftCard", id: UUID.idString(request.id) },
          { type: "GiftCardHistory", id: UUID.idString(request.id) },
          { type: "GetGiftCardHistoryPDF", id: UUID.idString(request.id) },
        ],
      }),
      enableOrDisableGiftCards: builder.mutation<
        EnableOrDisableGiftCardsRes,
        EnableOrDisableGiftCardsReq
      >({
        query: (r) => client.enableOrDisableGiftCards(r),
        async onQueryStarted(request, { dispatch, queryFulfilled }) {
          const patchResult = dispatch(
            companyApiSlice.util.updateQueryData("getCompany", {}, (draft) => {
              if (draft.company) {
                draft.company.isGiftCardsEnabled = request.isEnabled
              }
            })
          )
          queryFulfilled.catch(patchResult.undo)
        },
      }),
    }),
  })

export const {
  useGetGiftCardQuery,
  useGetGiftCardByNumberQuery,
  useLazyGetGiftCardByNumberQuery,
  useListGiftCardHistoryQuery,
  useBulkGenerateGiftCardNumbersMutation,
  useBulkImportGiftCardNumbersMutation,
  useLazyGetGiftCardHistoryPDFQuery,
  useDeactivateGiftCardMutation,
  useEnableOrDisableGiftCardsMutation,
} = giftcardsApiSlice
